import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import ApiIcon from '@mui/icons-material/Api';
import StorageIcon from '@mui/icons-material/Storage';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import SendIcon from '@mui/icons-material/Send';

import {
  activateLeadPerfection,
  autoMatchColumns,
  createMapping,
  fetchMappings,
  fetchMetadata,
  previewApiRecords,
  previewCsv,
  processManualUpload,
  sampleApiConnection,
  testApiConnection,
} from '@src/api/actions/dashboard/dataIntegrations';
import { useAuth } from '@src/context';
import paths from '@src/paths';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { useNavigate } from 'react-router-dom';
import { TourOverlay, TourTriggerButton, useTour } from './DataIntegrationWizardTour';

const STEPS_EMAIL = ['Connection Type', 'Which CRM?', 'Setup Instructions', 'Details', 'Map Columns', 'Preview & Activate'];
const STEPS_WITHOUT_SETUP = ['Connection Type', 'Details', 'Map Columns', 'Preview & Activate'];
const STEPS_MANUAL = ['Connection Type', 'Map Columns', 'Preview & Import'];
const STEPS_LEADPERFECTION = ['Connection Type', 'Which CRM?', 'LeadPerfection Setup'];

// ─── Draft persistence (localStorage) ──────────────────────────────
//
// The wizard is long and non-trivial to complete. Users frequently need to
// step away — collect credentials, ask IT for a webhook URL, etc. We save
// an in-progress draft to localStorage keyed by user id so they can come
// back and resume without losing work. We deliberately do NOT persist
// sampled API records (PII + storage quota) or sampleFile (File objects
// can't be serialized) — those get refetched/re-uploaded on resume.
const DRAFT_KEY_PREFIX = 'data-integration-wizard-draft:';
const DRAFT_STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadWizardDraft(userId) {
  if (!userId) return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.savedAt && Date.now() - parsed.savedAt > DRAFT_STALE_MS) {
      window.localStorage.removeItem(DRAFT_KEY_PREFIX + userId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveWizardDraft(userId, state) {
  if (!userId) return;
  try {
    window.localStorage.setItem(
      DRAFT_KEY_PREFIX + userId,
      JSON.stringify({ ...state, savedAt: Date.now() })
    );
  } catch {
    // Quota or access errors are non-fatal — the wizard still works in-memory.
  }
}

function clearWizardDraft(userId) {
  if (!userId) return;
  try {
    window.localStorage.removeItem(DRAFT_KEY_PREFIX + userId);
  } catch {
    // noop
  }
}

const INGEST_EMAIL = 'data@leadgurus.com';

const PRIMARY_CONNECTION_TYPES = [
  {
    value: 'email',
    label: 'Automated Email',
    description: 'Your CRM sends a report on a recurring schedule — we process it automatically.',
    icon: <EmailIcon sx={{ fontSize: 44 }} />,
    recommended: true,
  },
  {
    value: 'manual',
    label: 'Manual Upload',
    description: 'Upload a CSV or Excel file yourself — one-click reuploads any time.',
    icon: <UploadFileIcon sx={{ fontSize: 44 }} />,
    recommended: true,
  },
];

const ADVANCED_CONNECTION_TYPES = [
  {
    value: 'api',
    label: 'API Connection',
    description: 'We pull data from your REST API on a schedule.',
    icon: <ApiIcon sx={{ fontSize: 40 }} />,
  },
  {
    value: 'google_sheet',
    label: 'Google Sheet',
    description: 'We pull data from a shared Google spreadsheet.',
    icon: <StorageIcon sx={{ fontSize: 40 }} />,
  },
];

// Staff-only connection types — not shown to clients (SharePoint setup needs
// a Lead Gurus staff login + 2FA, so clients can't self-serve it).
const STAFF_CONNECTION_TYPES = [
  {
    value: 'sharepoint',
    label: 'SharePoint',
    description: 'We pull the newest lead file from a client\'s SharePoint folder. Staff-only — needs a one-time connect.',
    icon: <CloudSyncIcon sx={{ fontSize: 40 }} />,
  },
];

const ALL_CONNECTION_TYPES = [
  ...PRIMARY_CONNECTION_TYPES,
  ...ADVANCED_CONNECTION_TYPES,
  ...STAFF_CONNECTION_TYPES,
];

// ─── Step 1: Choose Connection Type ────────────────────────────────

function StepConnectionType({ value, onChange, isStaff }) {
  const advancedTypes = isStaff
    ? [...ADVANCED_CONNECTION_TYPES, ...STAFF_CONNECTION_TYPES]
    : ADVANCED_CONNECTION_TYPES;
  const renderCard = (type, primary) => (
    <Card
      key={type.value}
      variant={value === type.value ? 'elevation' : 'outlined'}
      sx={{
        flex: 1,
        minWidth: primary ? 260 : 220,
        border: value === type.value ? '2px solid' : undefined,
        borderColor: value === type.value ? 'primary.main' : undefined,
        position: 'relative',
      }}
    >
      {type.recommended && (
        <Chip
          label="Recommended"
          size="small"
          color="success"
          sx={{ position: 'absolute', top: 8, right: 8, fontSize: 11, height: 20 }}
        />
      )}
      <CardActionArea onClick={() => onChange(type.value)} sx={{ p: primary ? 3.5 : 2.5, textAlign: 'center' }}>
        {type.icon}
        <Typography variant={primary ? 'subtitle1' : 'subtitle2'} fontWeight={600} sx={{ mt: 1 }}>
          {type.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {type.description}
        </Typography>
      </CardActionArea>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        How does your team send backend data?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Most clients use one of the two recommended options. Pick whatever fits your current workflow — you can change this later.
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        data-tour-id="type-picker"
      >
        {PRIMARY_CONNECTION_TYPES.map((t) => renderCard(t, true))}
      </Stack>

      <Accordion
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          '&:before': { display: 'none' },
          borderRadius: 1,
          mt: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" color="text.secondary">
            More connection types (API, Google Sheet) — for technical users
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
            {advancedTypes.map((t) => renderCard(t, false))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

// ─── Step 1b: Setup Instructions (email only) ─────────────────────

function StepSetupInstructions({ confirmed, onConfirm }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(INGEST_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Set up your email automation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Before we configure the connection, set up an automated report in your CRM. Here's what we need:
      </Typography>

      <Stack spacing={2.5}>
        <Box
          data-tour-id="setup-email-address"
          sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1 }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            1. Send the report to this email address
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1"
              fontWeight={600}
              fontFamily="monospace"
              sx={{ bgcolor: 'white', px: 2, py: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            >
              {INGEST_EMAIL}
            </Typography>
            <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
              {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
            {copied && (
              <Typography variant="caption" color="success.main">Copied!</Typography>
            )}
          </Stack>
        </Box>

        <Box data-tour-id="setup-subject" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            2. Use a consistent subject line
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keep the same subject line every time (e.g., "Daily Lead Report"). We use this to identify your emails automatically.
          </Typography>
        </Box>

        <Box data-tour-id="setup-alltime" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            3. Use all-time data (strongly recommended)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set your report to include <strong>all-time data</strong>, not just the last 7 or 30 days.
            Leads generated months ago may convert today — all-time ensures we capture everything.
            Minimum: last 90 days.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            4. Schedule it daily
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set a daily schedule at a consistent time (e.g., 5:00 AM). We'll check for new data shortly after.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            5. Include as many columns as possible
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The more data you include, the better we can track your full funnel — from lead to appointment to revenue.
            This powers your ROI reporting and helps us optimize your campaigns for the outcomes that matter most.
            You'll map everything in the next step.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip label="Lead / Prospect ID" size="small" color="primary" variant="filled" />
            <Chip label="Created Date" size="small" color="primary" variant="filled" />
            <Chip label="Phone Number" size="small" color="primary" variant="filled" />
            <Chip label="Email Address" size="small" color="primary" variant="filled" />
            <Chip label="Appointment Date" size="small" color="success" variant="outlined" />
            <Chip label="Sold / Close Date" size="small" color="success" variant="outlined" />
            <Chip label="Gross Revenue" size="small" color="success" variant="outlined" />
            <Chip label="Net Revenue" size="small" color="primary" variant="filled" />
            <Chip label="ZIP Code" size="small" color="success" variant="outlined" />
            <Chip label="Demo (Yes/No)" size="small" variant="outlined" />
            <Chip label="Issue (Yes/No)" size="small" variant="outlined" />
            <Chip label="Status / Stage" size="small" variant="outlined" />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" color="primary" variant="filled" sx={{ width: 12, height: 12, mr: 0.5 }} /> Required
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" color="success" variant="outlined" sx={{ width: 12, height: 12, mr: 0.5 }} /> Recommended
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Chip label="" size="small" variant="outlined" sx={{ width: 12, height: 12, mr: 0.5 }} /> Optional
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            6. Use CSV format
          </Typography>
          <Typography variant="body2" color="text.secondary">
            CSV or Excel (.xlsx) attachments work best. Avoid PDF or embedded HTML tables.
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <FormControlLabel
        data-tour-id="setup-confirm"
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => onConfirm(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I've set up the email automation and sent at least one test email to <strong>{INGEST_EMAIL}</strong>
          </Typography>
        }
      />
    </Box>
  );
}

// ─── CRM Choice Step (shown after "Automated Email" is picked) ────
//
// LeadPerfection isn't really its own delivery mechanism — it's a specific
// CRM that uses the automated-email flow. So after a user picks "Automated
// Email", we ask which CRM they're on. LP has a fully preset path (no
// setup instructions, no column mapping); anything else falls through to
// the standard email flow.

function StepCrmChoice({ onPick }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Which CRM does your team use?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Picking your CRM lets us skip most of the setup. If you're not sure, choose <em>Other</em> and we'll walk you through it.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1, position: 'relative' }}>
          <Chip
            label="Recommended"
            size="small"
            color="success"
            sx={{ position: 'absolute', top: 8, right: 8, fontSize: 11, height: 20 }}
          />
          <CardActionArea onClick={() => onPick('leadperfection')} sx={{ p: 3, textAlign: 'center' }}>
            <HomeRepairServiceIcon sx={{ fontSize: 44 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
              LeadPerfection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Zero setup on your end — we'll email LP directly and handle the report configuration for you.
            </Typography>
          </CardActionArea>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardActionArea onClick={() => onPick('email')} sx={{ p: 3, textAlign: 'center' }}>
            <EmailIcon sx={{ fontSize: 44 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
              Other CRM / Custom report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Works with any CRM that can send a scheduled email — we'll walk you through it.
            </Typography>
          </CardActionArea>
        </Card>
      </Stack>
    </Box>
  );
}


// ─── LeadPerfection Setup Step ─────────────────────────────────────
//
// LP has a standard report + a known support contact (Kate at RJR), so this
// step collapses the whole flow into one screen: pick the client, give us
// the client's LP contact, then staff emails Kate and we auto-activate the
// EmailMapping with the preset column mapping. Client members can't email
// Kate directly (policy) — they trigger a notification to LG staff instead.

function StepLeadPerfection({
  config,
  onChange,
  clientOptions,
  onClientSearch,
  selectedClient,
  onClientChange,
  isSingleClient,
  isStaff,
  currentUser,
  lpMeta,
  submitting,
  result,
  onSubmit,
}) {
  const update = (field) => (e) => onChange({ ...config, [field]: e.target.value });
  const supportName = lpMeta?.support_contact_name || 'Kate Murch';
  const supportEmail = lpMeta?.support_contact_email || 'kmurch@rjrtechnology.com';
  const clientName = selectedClient?.name || selectedClient?.display_name || '';

  // Client members don't see the contact-name / contact-email fields — we
  // just use their own logged-in identity. Staff fills in the client's
  // contact because they may be setting this up on the client's behalf.
  const selfName = [currentUser?.first_name, currentUser?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim() || currentUser?.email || '';
  const selfEmail = currentUser?.email || '';
  const effectiveContactName = isStaff ? (config.client_contact_name || '') : selfName;
  const effectiveContactEmail = isStaff ? (config.client_contact_email || '') : selfEmail;

  // Build a client-side preview of the mailto so staff sees exactly what
  // they'll be sending before they click "Open email". Keep this in sync
  // with build_email_template in leadperfection_preset.py.
  const emailPreview = useMemo(() => {
    const cc = effectiveContactEmail ? [effectiveContactEmail] : [];
    const ccMention = effectiveContactName
      || effectiveContactEmail
      || 'their point of contact';
    const srsDisplay = config.srs_id || '[SRS_ID]';
    const subject = `LeadPerfection Report Setup — ${clientName || '[Client]'}`;
    const bodyLines = [
      `Hi ${supportName.split(' ')[0]},`,
      '',
      'Hope you\'re doing well!',
      '',
      `I'm reaching out on behalf of our client, ${clientName || '[Client]'}. They're a current LeadPerfection customer and would like to set up a custom Leads by Sub Source report — the same standard report we've set up for several of our other shared clients.`,
      '',
      `I've CC'd ${ccMention} from ${clientName || '[Client]'} so they can approve the setup.`,
      '',
      `Our SRS_ID: ${srsDisplay}`,
      '',
      'Requested configuration:',
      '  • Send to: data@leadgurus.com',
      '  • Frequency: daily, 5:00 AM EST',
      '  • Lookback: all-time (we\'re aware of the file-size considerations and are well under the limit)',
      '  • Required columns: ProspectID, phone, Zip, Email, entrydate, ApptDate, ContractDate, GrossAmount, NetAmount, EverSat, EverIssued',
      '',
    ];
    if (config.special_requests) {
      bodyLines.push('Additional notes from the client:', config.special_requests, '');
    }
    bodyLines.push('Appreciate your help getting this set up!', '', 'Thanks,', 'The LeadGurus team');
    return {
      to: supportEmail,
      cc,
      subject,
      body: bodyLines.join('\n'),
    };
  }, [
    effectiveContactName,
    effectiveContactEmail,
    config.srs_id,
    config.special_requests,
    clientName,
    supportName,
    supportEmail,
  ]);

  const mailtoHref = useMemo(() => {
    const params = new URLSearchParams();
    if (emailPreview.cc.length) params.set('cc', emailPreview.cc.join(','));
    params.set('subject', emailPreview.subject);
    params.set('body', emailPreview.body);
    return `mailto:${emailPreview.to}?${params.toString()}`;
  }, [emailPreview]);

  if (result) {
    const emailSent = result.email_sent !== false; // treat missing flag as success
    const severity = emailSent ? 'success' : 'warning';
    const headline = emailSent
      ? "You're all set"
      : "Integration created — email didn't go out";
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CheckCircleIcon
          sx={{ fontSize: 56, color: `${severity}.main`, mb: 1 }}
        />
        <Typography variant="h6" sx={{ mb: 1 }}>
          {headline}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
          {result.detail}
        </Typography>
        {!emailSent && (
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            component="a"
            href={mailtoHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open template in my email app
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        LeadPerfection setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        LP uses a standard report format across all our clients, so setup is almost fully automated.
        {isStaff
          ? " Confirm the client + LP contact below, then you'll send one prefilled email to Kate at RJR."
          : ' Fill this in and your LeadGurus rep will email LP on your behalf — no action needed from you after that.'}
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        {isSingleClient ? (
          <TextField
            label="Client"
            value={selectedClient?.name || ''}
            disabled
            helperText="Your account is linked to this client."
          />
        ) : (
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.name || ''}
            value={selectedClient}
            onChange={(_, v) => onClientChange(v)}
            onInputChange={onClientSearch}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            filterOptions={(x) => x}
            renderInput={(p) => (
              <TextField {...p} label="Client" required helperText="Which client is this LP integration for?" />
            )}
          />
        )}

        {isStaff ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Client contact name"
              placeholder="Jane Doe"
              value={config.client_contact_name || ''}
              onChange={update('client_contact_name')}
              helperText="Who at the client owns the LeadPerfection relationship? Used in the email."
            />
            <TextField
              fullWidth
              label="Client contact email"
              placeholder="person@client.com"
              value={config.client_contact_email || ''}
              onChange={update('client_contact_email')}
              helperText="We'll CC this person so they can approve the report setup."
            />
          </Stack>
        ) : (
          <Alert severity="success" variant="outlined">
            <Typography variant="body2">
              We'll use your contact details:{' '}
              <strong>{selfName || '(missing name)'}</strong>
              {selfEmail ? <> ({selfEmail})</> : null}.
              {' '}Your LeadGurus rep will CC you on the email to LeadPerfection.
            </Typography>
          </Alert>
        )}

        <TextField
          label="SRS_ID"
          placeholder="294"
          value={config.srs_id || ''}
          onChange={update('srs_id')}
          sx={{ maxWidth: 200 }}
          helperText="LeadPerfection's SRS_ID for this account. Kate needs this to route the report correctly."
        />

        <TextField
          label="Anything specific we should tell LeadPerfection? (optional)"
          placeholder="E.g. include only sold-deal breakdowns, custom date ranges, etc."
          value={config.special_requests || ''}
          onChange={update('special_requests')}
          multiline
          minRows={2}
          helperText="Leave blank to use our standard report."
        />

        {isStaff && (
          <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Email preview (to Kate)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {`To: ${emailPreview.to}\nCC: ${emailPreview.cc.join(', ')}\nSubject: ${emailPreview.subject}\n\n${emailPreview.body}`}
            </Typography>
          </Card>
        )}

        <Alert severity="info" icon={<HomeRepairServiceIcon />}>
          <Typography variant="body2">
            <strong>What happens next:</strong> Clicking below creates the integration with LP's
            standard column layout and emails {supportName} at LeadPerfection from{' '}
            <code>jack@leadgurus.com</code>
            {effectiveContactEmail ? <>, CC'ing <strong>{effectiveContactEmail}</strong> so they can approve</> : null}.
            Reports start importing automatically once she enables the daily report.
          </Typography>
        </Alert>

        <Box>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!selectedClient || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {submitting ? 'Sending…' : `Create & email ${supportName.split(' ')[0]}`}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}


// ─── Step 2: Connection Details ────────────────────────────────────

function StepDetails({ type, config, onChange, clientOptions, onClientSearch, selectedClient, onClientChange, isSingleClient, googleServiceAccountEmail }) {
  const update = (field) => (e) => onChange({ ...config, [field]: e.target.value });

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const authType = config.auth_type || 'none';
  const method = (config.method || 'GET').toUpperCase();

  // Parse optional JSON fields for body/headers. Empty means "no value".
  const parseOptionalJson = (raw) => {
    if (raw == null || String(raw).trim() === '') return { value: null, error: null };
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        return { value: null, error: 'Must be a JSON object (e.g. {"key": "value"}).' };
      }
      return { value: parsed, error: null };
    } catch (e) {
      return { value: null, error: `Invalid JSON: ${e.message}` };
    }
  };

  const bodyParsed = parseOptionalJson(config.json_body_text);
  const headersParsed = parseOptionalJson(config.json_headers_text);

  const runApiTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = {
        client_id: selectedClient?.id,
        api_endpoint: config.api_endpoint,
        auth_type: config.auth_type || 'none',
        api_key: config.api_key || '',
        api_key_header: config.api_key_header || 'X-API-Key',
        basic_username: config.basic_username || '',
        method: (config.method || 'GET').toUpperCase(),
      };
      if (headersParsed.value) payload.headers = headersParsed.value;
      if (bodyParsed.value) payload.body = bodyParsed.value;
      const res = await testApiConnection(payload);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({
        ok: false,
        detail: err?.response?.data?.detail || err?.message || 'Test failed.',
      });
    } finally {
      setTesting(false);
    }
  }, [config, headersParsed.value, bodyParsed.value, selectedClient?.id]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {type === 'email'
          ? 'Tell us about the email you set up'
          : type === 'api'
            ? 'Configure your API connection'
            : type === 'sharepoint'
              ? 'Configure the SharePoint folder'
              : 'Connect your Google Sheet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {type === 'email'
          ? 'Enter the details of the automated email you configured so we can find and process it.'
          : type === 'api'
            ? 'Provide the endpoint and credentials so we can pull your data.'
            : type === 'sharepoint'
              ? 'Tell us which SharePoint folder holds the lead files. You\'ll connect your login once after creating this.'
              : 'Share your spreadsheet so we can read your data.'}
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 500 }}>
        {isSingleClient ? (
          <TextField
            label="Client"
            value={selectedClient?.name || ''}
            disabled
            helperText="Your account is linked to this client."
          />
        ) : (
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.name || ''}
            value={selectedClient}
            onChange={(_, v) => onClientChange(v)}
            onInputChange={onClientSearch}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            filterOptions={(x) => x}
            renderInput={(params) => <TextField {...params} label="Client" required />}
          />
        )}

        {type === 'email' && (
          <>
            <TextField
              data-tour-id="details-sender"
              label="Sender Email Address"
              placeholder="reports@theircrm.com"
              value={config.email_sender || ''}
              onChange={update('email_sender')}
              required
              helperText="The 'From' address your CRM uses when it sends the report. Check a real email you've received from it and copy the sender exactly."
            />
            <TextField
              data-tour-id="details-subject"
              label="Email Subject Line"
              placeholder="Daily Lead Report"
              value={config.subject || ''}
              onChange={update('subject')}
              required
              helperText="Must match exactly — any change to the subject in your CRM will cause imports to stop. Copy-paste from a real email rather than typing it."
            />
            <TextField
              label="Attachment Filename Contains"
              placeholder="leads"
              value={config.filename_substring || ''}
              onChange={update('filename_substring')}
              helperText="Optional. Helps us pick the right file when an email has multiple attachments (e.g. 'leads' would match 'leads_2026.csv' and skip 'summary.pdf')."
            />

            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
            <Typography variant="body2" color="text.secondary">
              Just tell us when your CRM is set to send — we'll check for the email 15 minutes later automatically.
            </Typography>
          </>
        )}

        {type === 'api' && (
          <>
            <TextField
              data-tour-id="api-endpoint"
              label="API Endpoint URL"
              placeholder="https://api.example.com/v1/leads"
              value={config.api_endpoint || ''}
              onChange={update('api_endpoint')}
              required
              helperText="Full HTTPS URL we'll call on your schedule. We expect a JSON response — arrays of records or an object with a 'data' / 'leads' / 'items' / 'results' field."
            />

            <FormControl data-tour-id="api-auth">
              <InputLabel>Authentication</InputLabel>
              <Select
                value={authType}
                onChange={update('auth_type')}
                label="Authentication"
              >
                <MenuItem value="none">None (public endpoint)</MenuItem>
                <MenuItem value="bearer">Bearer Token</MenuItem>
                <MenuItem value="api_key">API Key (custom header)</MenuItem>
                <MenuItem value="basic">Basic Auth (username + password)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                {authType === 'none' && "We won't send any auth header. Most APIs require one."}
                {authType === 'bearer' && 'We\'ll send "Authorization: Bearer <your token>" on every request.'}
                {authType === 'api_key' && 'We\'ll send your key in a custom header (default: X-API-Key).'}
                {authType === 'basic' && 'We\'ll send a standard HTTP Basic Authorization header.'}
              </Typography>
            </FormControl>

            {authType === 'bearer' && (
              <TextField
                label="Bearer Token"
                placeholder="eyJhbGciOi... or sk_live_..."
                value={config.api_key || ''}
                onChange={update('api_key')}
                type="password"
                required
                helperText="Stored encrypted. Paste the token exactly — we'll add 'Bearer ' for you."
              />
            )}

            {authType === 'api_key' && (
              <>
                <TextField
                  label="Header Name"
                  placeholder="X-API-Key"
                  value={config.api_key_header || ''}
                  onChange={update('api_key_header')}
                  helperText="Common values: X-API-Key, X-Auth-Token, Api-Key. Check your API's docs."
                />
                <TextField
                  label="API Key"
                  value={config.api_key || ''}
                  onChange={update('api_key')}
                  type="password"
                  required
                  helperText="Stored encrypted. We'll send this as the header value above."
                />
              </>
            )}

            {authType === 'basic' && (
              <>
                <TextField
                  label="Username"
                  value={config.basic_username || ''}
                  onChange={update('basic_username')}
                  required
                />
                <TextField
                  label="Password"
                  value={config.api_key || ''}
                  onChange={update('api_key')}
                  type="password"
                  required
                  helperText="Stored encrypted."
                />
              </>
            )}

            <FormControl data-tour-id="api-method">
              <InputLabel>HTTP Method</InputLabel>
              <Select
                value={method}
                onChange={update('method')}
                label="HTTP Method"
              >
                <MenuItem value="GET">GET (retrieve data)</MenuItem>
                <MenuItem value="POST">POST (send a request body)</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Most read-only APIs use GET. Use POST if your API requires a JSON body (e.g. a date filter).
              </Typography>
            </FormControl>

            {method === 'POST' && (
              <TextField
                label="Request Body (JSON)"
                placeholder='{"from_date": "2024-01-01", "limit": 500}'
                value={config.json_body_text || ''}
                onChange={update('json_body_text')}
                multiline
                minRows={3}
                error={!!bodyParsed.error}
                helperText={bodyParsed.error || 'Sent as JSON on every request. Leave empty if your POST endpoint takes no body.'}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            )}

            <Accordion
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '&:before': { display: 'none' },
                borderRadius: 1,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Advanced (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <TextField
                    label="Custom Headers (JSON)"
                    placeholder='{"X-Client-Id": "abc123", "Accept": "application/json"}'
                    value={config.json_headers_text || ''}
                    onChange={update('json_headers_text')}
                    multiline
                    minRows={2}
                    error={!!headersParsed.error}
                    helperText={headersParsed.error || "Extra headers on top of your auth header. Don't duplicate Authorization here — it's set from your auth choice above."}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  />

                  <Divider />

                  <Typography variant="body2" color="text.secondary">
                    <strong>Pagination</strong> — only fill in if your API returns results across multiple pages.
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Page Param"
                      placeholder="page"
                      value={config.pagination_param || ''}
                      onChange={update('pagination_param')}
                      helperText="Query param for page number."
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Page Size Param"
                      placeholder="page_size"
                      value={config.page_size_param || ''}
                      onChange={update('page_size_param')}
                      helperText="Query param for page size."
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Start Page"
                      type="number"
                      value={config.initial_page ?? ''}
                      onChange={update('initial_page')}
                      helperText="Usually 1, some APIs use 0."
                      sx={{ width: 120 }}
                    />
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Box data-tour-id="api-test">
              <Button
                variant="outlined"
                size="small"
                onClick={runApiTest}
                disabled={!config.api_endpoint || testing || !!bodyParsed.error || !!headersParsed.error}
                startIcon={testing ? <CircularProgress size={14} /> : null}
              >
                {testing ? 'Testing…' : 'Test Connection'}
              </Button>
              {testResult && (
                <Alert
                  severity={testResult.ok ? 'success' : 'error'}
                  sx={{ mt: 1.5 }}
                >
                  {testResult.detail || (testResult.ok ? 'Connected.' : 'Connection failed.')}
                  {testResult.status_code ? ` (HTTP ${testResult.status_code})` : ''}
                </Alert>
              )}
            </Box>

            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        {type === 'sharepoint' && (
          <>
            <Alert severity="info" sx={{ mb: 1 }}>
              After you create this, the Data Integrations page will show a one-time{' '}
              <strong>Connect</strong> step — you sign in once on your Mac and the daily
              pull then runs automatically. Nothing is asked of the client.
            </Alert>
            <TextField
              label="SharePoint Site URL"
              placeholder="https://tenant.sharepoint.com/sites/Marketing-External"
              value={config.site_url || ''}
              onChange={update('site_url')}
              required
              helperText="The site web URL — everything up to and including /sites/<SiteName>."
            />
            <TextField
              label="Folder Path (server-relative)"
              placeholder="/sites/Marketing-External/Shared Documents/Partner Files/Lead Guru"
              value={config.folder_path || ''}
              onChange={update('folder_path')}
              required
              helperText="The folder holding the lead files, starting with /sites/… (include 'Shared Documents')."
            />
            <TextField
              label="Filename Contains"
              placeholder="Leads_"
              value={config.filename_substring || ''}
              onChange={update('filename_substring')}
              helperText="We pull the newest file whose name contains this text."
            />
            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        {type === 'google_sheet' && (
          <>
            {googleServiceAccountEmail && (
              <Alert data-tour-id="sheet-service-account" severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Before you continue:</strong> share your spreadsheet with this email (Viewer access is enough):
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    sx={{ bgcolor: 'white', px: 1.25, py: 0.5, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }}
                  >
                    {googleServiceAccountEmail}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(googleServiceAccountEmail)}
                    title="Copy email"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Alert>
            )}
            <TextField
              data-tour-id="sheet-id"
              label="Google Sheet URL or ID"
              placeholder="https://docs.google.com/spreadsheets/d/1A2B3C.../edit"
              value={config.spreadsheet_id || ''}
              onChange={update('spreadsheet_id')}
              required
              helperText="Paste the full URL or just the spreadsheet ID (the long string between /d/ and /edit)."
            />
            <TextField
              data-tour-id="sheet-range"
              label="Sheet & Range"
              placeholder="Sheet1!A:Z"
              value={config.worksheet_range || ''}
              onChange={update('worksheet_range')}
              helperText="Which tab and columns to read. Use the tab name as it appears at the bottom of the spreadsheet, e.g. 'Leads!A:Z' to read all columns A through Z."
            />
            <Divider />
            <Typography variant="subtitle2">Schedule</Typography>
          </>
        )}

        <FormControl data-tour-id="details-schedule">
          <InputLabel>Frequency</InputLabel>
          <Select
            value={config.schedule_type || 'daily'}
            onChange={update('schedule_type')}
            label="Frequency"
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="hourly">Every Hour</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label={type === 'email' ? 'When does your CRM send the email?' : 'Run Time'}
          type="time"
          value={config.run_time || '06:00'}
          onChange={update('run_time')}
          helperText={
            type === 'email'
              ? (() => {
                  // Show the client exactly when we'll poll so the +15min
                  // add is explicit, not hidden magic.
                  const sendTime = config.run_time || '06:00';
                  const [h, m] = sendTime.split(':').map(Number);
                  if (Number.isFinite(h) && Number.isFinite(m)) {
                    const pollMinutes = h * 60 + m + 15;
                    const ph = String(Math.floor(pollMinutes / 60) % 24).padStart(2, '0');
                    const pm = String(pollMinutes % 60).padStart(2, '0');
                    return `We'll check for new emails at ${ph}:${pm} (15 min after your CRM sends).`;
                  }
                  return 'We\'ll check for new emails 15 minutes after your CRM sends.';
                })()
              : 'When should we pull new data?'
          }
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
    </Box>
  );
}

// ─── Step 3: Column Mapping ────────────────────────────────────────

export const REQUIRED_SET = new Set(['prospect_id', 'created_date', 'net_amount']);
export const CONTACT_SET = new Set(['phone', 'email']);
export const BOOLEAN_FIELDS = ['demo', 'issue'];
export const RECOMMENDED_FIELDS = ['prospect_id', 'created_date', 'phone', 'email', 'appointment_date', 'sold_date', 'gross_amount', 'net_amount', 'zip_code', 'demo', 'issue'];
export const DATE_FIELDS = ['appointment_date', 'sold_date'];

// ─── Transform rule converters ──────────────────────────────────────
//
// The wizard's internal `transforms` shape (keyed `bool_<field>` /
// `date_<field>`) needs to be serialized into the backend's
// `transform_params.transformations` array on save, and parsed back
// into the UI shape when loading an existing mapping for edit.

// Serialize a date-rule's optional condition into the backend `when` clause.
// Operator vocabulary matches apply_custom_transformations.py.
function conditionToWhen(rule) {
  if (!rule.condColumn) return null;
  const cond = { field: [rule.condColumn] };
  switch (rule.condOp || 'equals') {
    case 'not_empty':
      cond.not_empty = true;
      break;
    case 'empty':
      cond.empty = true;
      break;
    case 'contains':
      cond.contains = rule.condValue;
      break;
    case 'not_contains':
      cond.not_contains = rule.condValue;
      break;
    case 'gt': {
      const num = Number(rule.condValue);
      if (Number.isNaN(num)) return null;
      cond.gt_num = num;
      break;
    }
    default:
      cond.eq = rule.condValue;
  }
  return { all: [cond] };
}

// Inverse of conditionToWhen — hydrate {condColumn, condOp, condValue} from a
// stored `when.all[0]` clause.
function conditionFromWhen(cond) {
  if (!cond) return {};
  const condColumn = Array.isArray(cond.field) ? cond.field[0] : cond.field;
  if (!condColumn) return {};
  const out = { condColumn };
  if (cond.not_empty) {
    out.condOp = 'not_empty';
  } else if (cond.empty || cond.is_blank) {
    out.condOp = 'empty';
  } else if ('contains' in cond) {
    out.condOp = 'contains';
    out.condValue = cond.contains;
  } else if ('not_contains' in cond) {
    out.condOp = 'not_contains';
    out.condValue = cond.not_contains;
  } else if ('gt_num' in cond) {
    out.condOp = 'gt';
    out.condValue = String(cond.gt_num);
  } else if ('eq' in cond) {
    out.condOp = 'equals';
    out.condValue = cond.eq;
  } else {
    return {};
  }
  return out;
}

export function buildTransformationsFromRules(transforms) {
  const out = [];
  Object.entries(transforms || {}).forEach(([key, rule]) => {
    if (!rule) return;
    if (key.startsWith('bool_')) {
      const field = key.replace('bool_', '');
      if (!rule.type || !rule.column) return;
      if (rule.type === 'contains' && rule.values?.length) {
        rule.values.forEach((matchVal) => {
          out.push({
            set: field,
            to: true,
            when: { all: [{ field: [rule.column], eq: matchVal }] },
          });
        });
      } else if (rule.type === 'not_empty') {
        out.push({
          set: field,
          to: true,
          when: { all: [{ field: [rule.column], not_empty: true }] },
        });
      }
    } else if (key.startsWith('date_')) {
      const field = key.replace('date_', '');
      if (!rule.type) return;
      const source = rule.type === 'copy_mapped' ? rule.source : rule.sourceColumn;
      if ((rule.type === 'copy_mapped' || rule.type === 'copy_csv') && source) {
        const entry = { set: field, to: { field: [source] } };
        const when = conditionToWhen(rule);
        if (when) entry.when = when;
        out.push(entry);
      }
    }
  });
  return out;
}

// Best-effort inverse — parses a stored transformations array back into the
// wizard's UI shape so the Edit / Remap flows can pre-populate the form.
// Lossy on edge cases, but covers every pattern the wizard writes.
export function rulesFromTransformations(transformations) {
  const out = {};
  (transformations || []).forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const field = entry.set;
    if (!field) return;

    // Boolean rules set `to: true` on demo/issue
    if (BOOLEAN_FIELDS.includes(field) && entry.to === true) {
      const cond = (entry.when && entry.when.all && entry.when.all[0]) || {};
      const column = Array.isArray(cond.field) ? cond.field[0] : cond.field;
      if (!column) return;
      const key = `bool_${field}`;
      const existing = out[key] || { type: '', column, values: [] };
      if ('eq' in cond) {
        existing.type = 'contains';
        existing.column = column;
        existing.values = [...(existing.values || []), cond.eq];
      } else if (cond.not_empty) {
        existing.type = 'not_empty';
        existing.column = column;
      }
      out[key] = existing;
      return;
    }

    // Date rules copy `to: {field: [X]}` onto appointment_date/sold_date
    if (DATE_FIELDS.includes(field) && entry.to && typeof entry.to === 'object') {
      const target = Array.isArray(entry.to.field) ? entry.to.field[0] : entry.to.field;
      if (!target) return;
      const key = `date_${field}`;
      const cond = (entry.when && entry.when.all && entry.when.all[0]) || null;
      // If target matches a backend field, treat as copy_mapped; otherwise
      // assume copy_csv. Callers can always tweak post-load.
      const backendFieldTargets = new Set(['created_date', 'appointment_date', 'sold_date']);
      if (backendFieldTargets.has(target)) {
        out[key] = { type: 'copy_mapped', source: target, ...conditionFromWhen(cond) };
      } else {
        out[key] = { type: 'copy_csv', sourceColumn: target, ...conditionFromWhen(cond) };
      }
    }
  });
  return out;
}

// Storage shape for column_mappings is `{header: "field"}` for one target and
// `{header: ["f1", "f2"]}` for many. Normalize either form into an array for
// the UI, and collapse back to a string when there's exactly one target so
// existing single-mapping rows don't churn in the DB.
export function mappingTargets(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

export function cleanColumnMappings(mappings) {
  const out = {};
  Object.entries(mappings || {}).forEach(([header, value]) => {
    const targets = mappingTargets(value);
    if (targets.length === 1) out[header] = targets[0];
    else if (targets.length > 1) out[header] = targets;
  });
  return out;
}

const BOOL_RULE_TYPES = [
  { value: '', label: 'Not applicable' },
  { value: 'contains', label: 'When a column contains a value' },
  { value: 'not_empty', label: 'When a column is not empty' },
];

const DATE_RULE_TYPES = [
  { value: '', label: 'Leave empty (not available)' },
  { value: 'copy_mapped', label: 'Always copy from a mapped field' },
  { value: 'copy_csv', label: 'Copy from a CSV column (with optional condition)' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'gt', label: 'Greater than (number)' },
  { value: 'not_empty', label: 'Is not empty' },
  { value: 'empty', label: 'Is empty' },
];

const NO_VALUE_OPERATORS = new Set(['not_empty', 'empty']);

function describeCondition(rule) {
  if (!rule.condColumn) return '';
  const op = rule.condOp || 'equals';
  if (op === 'not_empty') return `when "${rule.condColumn}" has any value`;
  if (op === 'empty') return `when "${rule.condColumn}" is empty`;
  const opLabel = (CONDITION_OPERATORS.find((o) => o.value === op) || {}).label || op;
  return `when "${rule.condColumn}" ${opLabel.toLowerCase()} "${rule.condValue || ''}"`;
}

export function StepColumnMapping({
  connectionType,
  mappings,
  onMappingsChange,
  headers,
  onFileUpload,
  onApiSample,
  apiSampleError,
  apiSampleInfo,
  backendFields,
  fieldLabels,
  uploading,
  suggestions,
  transforms,
  onTransformsChange,
  sampleValues,
}) {
  const fileRef = useRef(null);
  const isApi = connectionType === 'api';

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileUpload(file);
  };

  const usedFields = useMemo(() => {
    const used = new Set();
    for (const value of Object.values(mappings)) {
      mappingTargets(value).forEach((f) => used.add(f));
    }
    return used;
  }, [mappings]);

  const hasContact = usedFields.has('phone') || usedFields.has('email');
  const missingRequired = [...REQUIRED_SET].filter((f) => !usedFields.has(f));

  const unmappedDateFields = useMemo(() =>
    DATE_FIELDS.filter((f) => !usedFields.has(f)),
    [usedFields]
  );

  // Mapped backend fields that can serve as date sources
  const mappedDateSources = useMemo(() =>
    ['created_date', 'appointment_date', 'sold_date'].filter((f) => usedFields.has(f)),
    [usedFields]
  );

  const unmappedBoolFields = useMemo(() =>
    BOOLEAN_FIELDS.filter((f) => !usedFields.has(f)),
    [usedFields]
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Map your columns to our fields
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isApi
          ? "We'll call your endpoint once and read the field names from the first records in the response. The more fields you map, the richer your ROI reporting will be."
          : "Upload a sample CSV or Excel file (.csv, .xlsx, .xls) from your report. We'll auto-detect your columns and suggest the best matches. The more fields you map, the richer your ROI reporting will be."}
      </Typography>

      {isApi ? (
        <Box sx={{ mb: 3 }}>
          <Button
            data-tour-id="columns-upload"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            onClick={onApiSample}
            disabled={uploading}
          >
            {headers.length ? 'Re-fetch sample from API' : 'Fetch sample from API'}
          </Button>
          {apiSampleInfo && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Sampled {apiSampleInfo.record_count} record{apiSampleInfo.record_count === 1 ? '' : 's'}
              {apiSampleInfo.record_path ? ` from "${apiSampleInfo.record_path}"` : ' (top-level array)'}.
            </Typography>
          )}
          {apiSampleError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: apiSampleError.preview ? 1 : 0 }}>
                {apiSampleError.detail}
                {apiSampleError.status_code ? ` (HTTP ${apiSampleError.status_code})` : ''}
              </Typography>
              {apiSampleError.preview && (
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontSize: 12,
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    maxHeight: 320,
                    overflow: 'auto',
                    m: 0,
                  }}
                >
                  {apiSampleError.preview}
                </Box>
              )}
            </Alert>
          )}
        </Box>
      ) : (
        <>
          <Button
            data-tour-id="columns-upload"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            sx={{ mb: 3 }}
          >
            {headers.length ? 'Re-upload Sample File' : 'Upload Sample CSV or Excel'}
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            hidden
            onChange={handleFile}
          />
        </>
      )}

      {headers.length > 0 && (
        <>
          {/* Field coverage chips */}
          <Box
            data-tour-id="columns-coverage"
            sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Field Coverage
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {RECOMMENDED_FIELDS.map((field) => (
                <Chip
                  key={field}
                  label={fieldLabels[field] || field}
                  size="small"
                  color={usedFields.has(field) ? 'success' : (REQUIRED_SET.has(field) || CONTACT_SET.has(field)) ? 'error' : 'default'}
                  variant={usedFields.has(field) ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>

          {missingRequired.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Required: {missingRequired.map((f) => fieldLabels[f] || f).join(', ')}
            </Alert>
          )}
          {!hasContact && (
            <Alert severity="error" sx={{ mb: 2 }}>
              At least one contact field (Phone or Email) must be mapped.
            </Alert>
          )}

          <TableContainer data-tour-id="columns-table" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{isApi ? 'API Field' : 'Your File Column'}</TableCell>
                  <TableCell>Maps To</TableCell>
                  <TableCell width={80}>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {headers.map((header) => {
                  const currentArr = mappingTargets(mappings[header]);
                  const suggestion = suggestions?.suggestions?.[header];
                  const confidence = suggestion?.confidence;
                  const examples = sampleValues?.[header] || [];
                  const shownExamples = examples.slice(0, 2);

                  return (
                    <TableRow key={header}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {header}
                        </Typography>
                        {examples.length > 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            title={examples.join('  •  ')}
                            sx={{
                              display: 'block',
                              fontFamily: 'monospace',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 260,
                            }}
                          >
                            e.g. {shownExamples.join(', ')}
                            {examples.length > shownExamples.length ? ', …' : ''}
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: 'block', fontStyle: 'italic' }}
                          >
                            (no sample value)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            multiple
                            value={currentArr}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const next = Array.isArray(raw) ? raw : [raw];
                              const nextMappings = { ...mappings };
                              if (next.length === 0) delete nextMappings[header];
                              else if (next.length === 1) nextMappings[header] = next[0];
                              else nextMappings[header] = next;
                              onMappingsChange(nextMappings);
                            }}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected || selected.length === 0) {
                                return <em>-- Skip --</em>;
                              }
                              return (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {selected.map((f) => (
                                    <Chip key={f} label={fieldLabels[f] || f} size="small" />
                                  ))}
                                </Box>
                              );
                            }}
                          >
                            {backendFields.map((field) => {
                              // One CSV column → many DB fields is allowed; reverse
                              // (many CSV columns → one DB field) is still blocked:
                              // a field is "taken" only if some OTHER row maps it.
                              const takenElsewhere = usedFields.has(field) && !currentArr.includes(field);
                              return (
                                <MenuItem key={field} value={field} disabled={takenElsewhere}>
                                  <Checkbox checked={currentArr.includes(field)} size="small" sx={{ p: 0.5, mr: 1 }} />
                                  {fieldLabels[field] || field}
                                  {takenElsewhere ? ' (already mapped)' : ''}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {confidence != null && (
                          <Chip
                            label={`${Math.round(confidence * 100)}%`}
                            size="small"
                            color={confidence >= 0.9 ? 'success' : confidence >= 0.75 ? 'warning' : 'default'}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Date field rules for unmapped appointment_date / sold_date */}
          {unmappedDateFields.length > 0 && (
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Missing date fields
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your data doesn't have columns mapped for these fields. Copy from another field, optionally with a condition.
              </Typography>
              <Stack spacing={3}>
                {unmappedDateFields.map((field) => {
                  const rule = transforms[`date_${field}`] || {};
                  const ruleType = rule.type || '';

                  // Shared optional-condition editor: both "copy from a mapped
                  // field" and "copy from a CSV column" can be gated on a row
                  // condition evaluated against the CSV columns.
                  const conditionEditor = (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Condition (optional) — only set when:
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <InputLabel>CSV column</InputLabel>
                          <Select
                            value={rule.condColumn || ''}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`date_${field}`]: { ...rule, condColumn: e.target.value, condOp: rule.condOp || 'equals' },
                            })}
                            label="CSV column"
                          >
                            <MenuItem value=""><em>No condition</em></MenuItem>
                            {headers.map((h) => (
                              <MenuItem key={h} value={h}>{h}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {rule.condColumn && (
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={rule.condOp || 'equals'}
                              onChange={(e) => onTransformsChange({
                                ...transforms,
                                [`date_${field}`]: { ...rule, condOp: e.target.value },
                              })}
                              label="Operator"
                            >
                              {CONDITION_OPERATORS.map((op) => (
                                <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {rule.condColumn && !NO_VALUE_OPERATORS.has(rule.condOp || 'equals') && (
                          <TextField
                            size="small"
                            label="Value"
                            value={rule.condValue || ''}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`date_${field}`]: { ...rule, condValue: e.target.value },
                            })}
                            sx={{ minWidth: 120 }}
                          />
                        )}
                      </Stack>
                    </>
                  );

                  const ruleSource = ruleType === 'copy_mapped' ? rule.source : rule.sourceColumn;
                  const ruleSummary = ruleSource && (
                    <Typography variant="caption" color="text.secondary">
                      {rule.condColumn
                        ? `Set ${fieldLabels[field] || field} = "${ruleSource}" ${describeCondition(rule)}`
                        : `Always set ${fieldLabels[field] || field} from "${ruleSource}"`}
                    </Typography>
                  );

                  return (
                    <Box key={field}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {fieldLabels[field] || field}
                      </Typography>
                      <Stack spacing={1.5}>
                        {/* Rule type */}
                        <FormControl size="small" sx={{ maxWidth: 400 }}>
                          <InputLabel>How to populate</InputLabel>
                          <Select
                            value={ruleType}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`date_${field}`]: { type: e.target.value },
                            })}
                            label="How to populate"
                          >
                            {DATE_RULE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Copy from mapped field, optionally gated on a condition */}
                        {ruleType === 'copy_mapped' && (
                          <>
                            <FormControl size="small" sx={{ maxWidth: 400 }}>
                              <InputLabel>Source field</InputLabel>
                              <Select
                                value={rule.source || ''}
                                onChange={(e) => onTransformsChange({
                                  ...transforms,
                                  [`date_${field}`]: { ...rule, source: e.target.value },
                                })}
                                label="Source field"
                              >
                                {mappedDateSources
                                  .filter((f) => f !== field)
                                  .map((f) => (
                                    <MenuItem key={f} value={f}>{fieldLabels[f] || f}</MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                            {conditionEditor}
                            {ruleSummary}
                          </>
                        )}

                        {/* Copy from CSV column with optional condition */}
                        {ruleType === 'copy_csv' && (
                          <>
                            <FormControl size="small" sx={{ maxWidth: 400 }}>
                              <InputLabel>Copy date from CSV column</InputLabel>
                              <Select
                                value={rule.sourceColumn || ''}
                                onChange={(e) => onTransformsChange({
                                  ...transforms,
                                  [`date_${field}`]: { ...rule, sourceColumn: e.target.value },
                                })}
                                label="Copy date from CSV column"
                              >
                                {headers.map((h) => (
                                  <MenuItem key={h} value={h}>{h}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {conditionEditor}
                            {ruleSummary}
                          </>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Boolean field rules for unmapped demo/issue */}
          {unmappedBoolFields.length > 0 && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Outcome fields (Demo / Issue)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your data doesn't have a direct column for these. Set a rule to determine them from other columns.
              </Typography>
              <Stack spacing={3}>
                {unmappedBoolFields.map((field) => {
                  const rule = transforms[`bool_${field}`] || {};
                  const ruleType = rule.type || '';
                  const selectedValues = rule.values || [];
                  const columnValues = (rule.column && sampleValues?.[rule.column]) || [];

                  return (
                    <Box key={field}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {fieldLabels[field] || field}
                      </Typography>
                      <Stack spacing={1.5}>
                        <FormControl size="small" sx={{ maxWidth: 350 }}>
                          <InputLabel>Rule type</InputLabel>
                          <Select
                            value={ruleType}
                            onChange={(e) => onTransformsChange({
                              ...transforms,
                              [`bool_${field}`]: { type: e.target.value, column: '', values: [] },
                            })}
                            label="Rule type"
                          >
                            {BOOL_RULE_TYPES.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {(ruleType === 'contains' || ruleType === 'not_empty') && (
                          <FormControl size="small" sx={{ maxWidth: 350 }}>
                            <InputLabel>CSV Column</InputLabel>
                            <Select
                              value={rule.column || ''}
                              onChange={(e) => onTransformsChange({
                                ...transforms,
                                [`bool_${field}`]: { ...rule, column: e.target.value, values: [] },
                              })}
                              label="CSV Column"
                            >
                              {headers.map((h) => (
                                <MenuItem key={h} value={h}>{h}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {ruleType === 'contains' && rule.column && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Select which values mean "{fieldLabels[field] || field}":
                            </Typography>
                            {columnValues.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {columnValues.map((val) => {
                                  const isSelected = selectedValues.includes(val);
                                  return (
                                    <Chip
                                      key={val}
                                      label={val}
                                      size="small"
                                      variant={isSelected ? 'filled' : 'outlined'}
                                      color={isSelected ? 'primary' : 'default'}
                                      onClick={() => {
                                        const next = isSelected
                                          ? selectedValues.filter((v) => v !== val)
                                          : [...selectedValues, val];
                                        onTransformsChange({
                                          ...transforms,
                                          [`bool_${field}`]: { ...rule, values: next },
                                        });
                                      }}
                                      sx={{ cursor: 'pointer' }}
                                    />
                                  );
                                })}
                              </Box>
                            ) : (
                              <TextField
                                size="small"
                                label="Values (comma separated)"
                                placeholder='e.g. Demo, Demo Sold, Appt Confirmed'
                                value={selectedValues.join(', ')}
                                onChange={(e) => {
                                  const vals = e.target.value
                                    .split(',')
                                    .map((v) => v.trim())
                                    .filter(Boolean);
                                  onTransformsChange({
                                    ...transforms,
                                    [`bool_${field}`]: { ...rule, values: vals },
                                  });
                                }}
                                sx={{ maxWidth: 400 }}
                              />
                            )}
                            {selectedValues.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {selectedValues.length} value{selectedValues.length !== 1 ? 's' : ''} selected
                              </Typography>
                            )}
                          </Box>
                        )}

                        {ruleType === 'not_empty' && rule.column && (
                          <Typography variant="body2" color="text.secondary">
                            {fieldLabels[field] || field} will be set to Yes when "{rule.column}" has any value.
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Step 4: Preview & Activate ────────────────────────────────────

function StepPreview({ previewData, loading, error, onRunPreview, isApi }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Running preview...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!previewData) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Almost there — let's verify everything looks right
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {isApi
            ? "We'll run the sample records we fetched from your API through the ingestion pipeline and show you exactly how each record will appear. If something looks off, you can go back and adjust."
            : "We'll process your sample file with the mappings you configured and show you exactly how the data will appear. If something looks off, you can go back and adjust."}
        </Typography>
        <Button data-tour-id="preview-run" variant="contained" onClick={onRunPreview}>
          Run Preview
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Preview results
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review the parsed data below. If everything looks correct, activate the connection — we'll start importing automatically on schedule.
      </Typography>

      <Stack data-tour-id="preview-counts" direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700}>
            {previewData.total_rows}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Rows
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="success.main">
            {previewData.success_count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Parsed OK
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ flex: 1, p: 2, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color="error.main">
            {previewData.error_count}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Errors
          </Typography>
        </Card>
      </Stack>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Sample Rows (first {previewData.preview_rows.length})
      </Typography>
      {(() => {
        // Collect the full union of keys across every preview row so we
        // don't drop columns when the first row happens to be missing a
        // mapped value. Preference order: parsed (post-mapping) first,
        // else raw (pre-mapping), else empty.
        const allKeys = [];
        const seen = new Set();
        previewData.preview_rows.forEach((row) => {
          const data = row.parsed || row.raw || {};
          Object.keys(data).forEach((k) => {
            if (!seen.has(k)) {
              seen.add(k);
              allKeys.push(k);
            }
          });
        });
        return (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  {allKeys.map((key) => (
                    <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{key}</TableCell>
                  ))}
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.preview_rows.map((row) => {
                  const displayData = row.parsed || row.raw || {};
                  return (
                    <TableRow key={row.row_number}>
                      <TableCell>{row.row_number}</TableCell>
                      {allKeys.map((key) => (
                        <TableCell
                          key={key}
                          sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={String(displayData[key] ?? '')}
                        >
                          {String(displayData[key] ?? '')}
                        </TableCell>
                      ))}
                      <TableCell>
                        {row.error ? (
                          <Chip label="Error" color="error" size="small" title={row.error} />
                        ) : (
                          <Chip label="OK" color="success" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        );
      })()}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Scroll horizontally to see all {
          (() => {
            const keysSet = new Set();
            previewData.preview_rows.forEach((row) => {
              const data = row.parsed || row.raw || {};
              Object.keys(data).forEach((k) => keysSet.add(k));
            });
            return keysSet.size;
          })()
        } mapped fields. Hover a cell to see the full value.
      </Typography>
    </Box>
  );
}

// ─── Main Wizard ───────────────────────────────────────────────────

export default function DataIntegrationWizardPage() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const user = state?.user;

  // ── Resolve accessible clients from membership context ──
  // Uses the same hook as Zip Coverage / Client Zips / Reports so the
  // staff dropdown always shows every active client (paginated) and
  // supports search-as-you-type. Non-staff users see only clients they
  // have membership to — the backend applies that scoping.
  const {
    options: autocompleteOptions,
    handleInputChange: handleClientSearch,
    loading: clientsLoading,
  } = useClientAutocomplete();

  // Downstream code expects {id, name} — map from the hook's
  // {value, label} shape so nothing else has to change.
  const clientOptions = useMemo(
    () => autocompleteOptions.map((o) => ({ id: o.value, name: o.label })),
    [autocompleteOptions]
  );

  const [selectedClient, setSelectedClient] = useState(null);

  const isStaff = user?.is_staff || user?.is_superuser;

  // Auto-select when a non-staff user has exactly one accessible client.
  useEffect(() => {
    if (!selectedClient && !isStaff && clientOptions.length === 1) {
      setSelectedClient(clientOptions[0]);
    }
  }, [clientOptions, selectedClient, isStaff]);

  const isSingleClient = !isStaff && clientOptions.length === 1 && selectedClient;

  const [activeStep, setActiveStep] = useState(0);
  const [connectionType, setConnectionType] = useState('');
  const [setupConfirmed, setSetupConfirmed] = useState(false);
  const [config, setConfig] = useState({ schedule_type: 'daily', run_time: '06:00' });

  // Email-flavored flows (standard Email + LeadPerfection) share a CRM
  // choice step right after Connection Type. From there, LP goes straight
  // to its one-shot setup while "Other CRM" continues through the full
  // setup instructions / details / map columns / preview flow.
  const isManual = connectionType === 'manual';
  const isLeadPerfection = connectionType === 'leadperfection';
  const isEmail = connectionType === 'email';
  const hasEmailFlavor = isEmail || isLeadPerfection;
  const hasSetupStep = isEmail; // legacy alias used below for readability
  const steps = isLeadPerfection
    ? STEPS_LEADPERFECTION
    : isEmail
      ? STEPS_EMAIL
      : isManual
        ? STEPS_MANUAL
        : STEPS_WITHOUT_SETUP;

  const STEP_CRM = hasEmailFlavor ? 1 : -1;
  const STEP_SETUP = isEmail ? 2 : -1;
  const STEP_LP = isLeadPerfection ? 2 : -1;
  const STEP_DETAILS = isEmail ? 3 : (isManual || isLeadPerfection ? -1 : 1);
  const STEP_COLUMNS = isLeadPerfection ? -1 : (isManual ? 1 : (isEmail ? 4 : 2));
  const STEP_PREVIEW = isLeadPerfection ? -1 : (isManual ? 2 : (isEmail ? 5 : 3));
  const [columnMappings, setColumnMappings] = useState({});
  const [dateTransforms, setDateTransforms] = useState({});
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [sampleValues, setSampleValues] = useState({});
  const [suggestions, setSuggestions] = useState(null);
  const [sampleFile, setSampleFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lpSubmitting, setLpSubmitting] = useState(false);
  const [lpResult, setLpResult] = useState(null);

  // Draft persistence: prompt-on-mount + auto-save on change + pending client
  // id for restoring selectedClient once clientOptions load.
  const draftUserId = user?.id;
  const [draftPrompt, setDraftPrompt] = useState(null);
  const [draftChecked, setDraftChecked] = useState(false);
  const [pendingClientId, setPendingClientId] = useState(null);

  useEffect(() => {
    if (!draftUserId || draftChecked) return;
    const draft = loadWizardDraft(draftUserId);
    if (draft && draft.connectionType) {
      setDraftPrompt(draft);
    }
    setDraftChecked(true);
  }, [draftUserId, draftChecked]);

  const resumeDraft = useCallback(() => {
    if (!draftPrompt) return;
    setConnectionType(draftPrompt.connectionType || '');
    setConfig(draftPrompt.config || { schedule_type: 'daily', run_time: '06:00' });
    setColumnMappings(draftPrompt.columnMappings || {});
    setDateTransforms(draftPrompt.dateTransforms || {});
    setActiveStep(draftPrompt.activeStep || 0);
    setSetupConfirmed(!!draftPrompt.setupConfirmed);
    if (draftPrompt.selectedClientId) {
      const opt = clientOptions.find((c) => c.id === draftPrompt.selectedClientId);
      if (opt) {
        setSelectedClient(opt);
      } else {
        setPendingClientId(draftPrompt.selectedClientId);
      }
    }
    setDraftPrompt(null);
  }, [draftPrompt, clientOptions]);

  const discardDraft = useCallback(() => {
    clearWizardDraft(draftUserId);
    setDraftPrompt(null);
  }, [draftUserId]);

  // Resolve the saved client id once client options finish loading. Staff
  // users may need to type/search to load their client list — this effect
  // keeps retrying as new options stream in.
  useEffect(() => {
    if (!pendingClientId) return;
    const opt = clientOptions.find((c) => c.id === pendingClientId);
    if (opt) {
      setSelectedClient(opt);
      setPendingClientId(null);
    }
  }, [pendingClientId, clientOptions]);

  // Auto-save current wizard state as a draft, debounced. Skip while the
  // resume prompt is still showing so we don't overwrite the saved draft
  // before the user decides whether to resume it.
  useEffect(() => {
    if (!draftUserId || !draftChecked || draftPrompt) return;
    if (!connectionType) return;
    const timer = setTimeout(() => {
      saveWizardDraft(draftUserId, {
        connectionType,
        config,
        columnMappings,
        dateTransforms,
        activeStep,
        setupConfirmed,
        selectedClientId: selectedClient?.id || null,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [
    draftUserId,
    draftChecked,
    draftPrompt,
    connectionType,
    config,
    columnMappings,
    dateTransforms,
    activeStep,
    setupConfirmed,
    selectedClient?.id,
  ]);

  useEffect(() => {
    fetchMetadata()
      .then((res) => setMetadata(res.data))
      .catch(() => {});
  }, []);

  const backendFields = metadata?.backend_fields || [];
  const fieldLabels = metadata?.field_labels || {};

  const applyAutoMatchResult = useCallback((data) => {
    setCsvHeaders(
      Object.keys(data.suggestions || {}).concat(data.unmapped || [])
    );
    setSuggestions(data);
    setSampleValues(data.sample_values || {});

    const auto = {};
    for (const [header, info] of Object.entries(data.suggestions || {})) {
      if (info.field) auto[header] = info.field;
    }
    setColumnMappings(auto);
  }, []);

  const handleFileUpload = useCallback(
    async (file) => {
      setUploading(true);
      setSampleFile(file);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await autoMatchColumns(formData);
        applyAutoMatchResult(res.data);
      } catch (err) {
        console.error('Auto-match failed:', err);
      } finally {
        setUploading(false);
      }
    },
    [applyAutoMatchResult]
  );

  const [apiSampleError, setApiSampleError] = useState(null);
  const [apiSampleInfo, setApiSampleInfo] = useState(null);
  const [apiSampleRecords, setApiSampleRecords] = useState([]);

  const handleApiSample = useCallback(async () => {
    if (!selectedClient?.id || !config.api_endpoint) return;
    setUploading(true);
    setApiSampleError(null);
    setApiSampleInfo(null);

    const parseOptionalJson = (raw) => {
      if (raw == null || String(raw).trim() === '') return null;
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
      } catch {
        return null;
      }
    };

    try {
      const payload = {
        client_id: selectedClient.id,
        api_endpoint: config.api_endpoint,
        auth_type: config.auth_type || 'none',
        api_key: config.api_key || '',
        api_key_header: config.api_key_header || 'X-API-Key',
        basic_username: config.basic_username || '',
        method: (config.method || 'GET').toUpperCase(),
      };
      const parsedHeaders = parseOptionalJson(config.json_headers_text);
      const parsedBody = parseOptionalJson(config.json_body_text);
      if (parsedHeaders) payload.headers = parsedHeaders;
      if (parsedBody) payload.body = parsedBody;

      const res = await sampleApiConnection(payload);
      const data = res.data;
      if (!data.ok) {
        setApiSampleError({
          detail: data.detail || 'Could not sample the API.',
          preview: data.preview || '',
          status_code: data.status_code,
        });
        setApiSampleRecords([]);
        return;
      }
      applyAutoMatchResult(data);
      setApiSampleInfo({
        record_count: data.record_count,
        record_path: data.record_path,
      });
      setApiSampleRecords(Array.isArray(data.records) ? data.records : []);
    } catch (err) {
      const body = err?.response?.data || {};
      setApiSampleError({
        detail: body.detail || err?.message || 'Failed to sample API.',
        preview: body.preview || '',
        status_code: body.status_code,
      });
    } finally {
      setUploading(false);
    }
  }, [selectedClient?.id, config, applyAutoMatchResult]);

  const handleRunPreview = useCallback(async () => {
    const isApi = connectionType === 'api';
    if (!isApi && !sampleFile) return;
    if (isApi && apiSampleRecords.length === 0) {
      setPreviewError('Fetch a sample from the API first in step 3.');
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const cleanMappings = cleanColumnMappings(columnMappings);
      const transformations = buildTransformationsFromRules(dateTransforms);
      const transformParams = transformations.length > 0 ? { transformations } : {};

      let res;
      if (isApi) {
        res = await previewApiRecords({
          column_mappings: cleanMappings,
          transform_params: transformParams,
          records: apiSampleRecords,
        });
      } else {
        const formData = new FormData();
        formData.append('file', sampleFile);
        formData.append('column_mappings', JSON.stringify(cleanMappings));
        if (transformations.length > 0) {
          formData.append('transform_params', JSON.stringify(transformParams));
        }
        res = await previewCsv(formData);
      }
      setPreviewData(res.data);
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || err?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  }, [connectionType, sampleFile, apiSampleRecords, columnMappings, dateTransforms]);

  const handleLpSubmit = useCallback(async () => {
    if (!selectedClient?.id) return;
    setLpSubmitting(true);
    setSaveError(null);
    try {
      // Client members submit on their own behalf — use their logged-in
      // identity as the contact. Staff picks the client contact explicitly.
      const contactName = isStaff
        ? (config.client_contact_name || '')
        : [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.email || '';
      const contactEmail = isStaff
        ? (config.client_contact_email || '')
        : user?.email || '';

      const res = await activateLeadPerfection({
        client: selectedClient.id,
        client_contact_name: contactName,
        client_contact_email: contactEmail,
        srs_id: config.srs_id || '',
        special_requests: config.special_requests || '',
      });
      setLpResult(res.data);
    } catch (err) {
      setSaveError(
        err?.response?.data?.detail || err?.message || 'Failed to submit LeadPerfection request.'
      );
    } finally {
      setLpSubmitting(false);
    }
  }, [selectedClient, config.client_contact_name, config.client_contact_email, config.srs_id, config.special_requests, isStaff, user]);

  const handleActivate = useCallback(async () => {
    setSaving(true);
    setSaveError(null);

    const cleanMappings = cleanColumnMappings(columnMappings);

    // Build transform_params from date rules + boolean rules using the
    // shared converter so Remap dialog and Wizard stay in sync.
    const transformations = buildTransformationsFromRules(dateTransforms);
    const transformParams = transformations.length > 0 ? { transformations } : {};

    // Email flow: the UI's "Run Time" field stores when the CRM *sends* the
    // email — server-side we poll 15 minutes later. Other flows: the field
    // is the actual poll time, so no shift.
    const rawRunTime = config.run_time || '06:00';
    const resolvedRunTime = (() => {
      if (connectionType !== 'email') return rawRunTime;
      const [h, m] = rawRunTime.split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return rawRunTime;
      const total = h * 60 + m + 15;
      const ph = String(Math.floor(total / 60) % 24).padStart(2, '0');
      const pm = String(total % 60).padStart(2, '0');
      return `${ph}:${pm}`;
    })();

    const payload = {
      client: selectedClient?.id,
      schedule_type: config.schedule_type || 'daily',
      run_time: resolvedRunTime,
      column_mappings: cleanMappings,
      transform_params: transformParams,
    };

    if (connectionType === 'email') {
      payload.email_sender = config.email_sender;
      payload.subject = config.subject;
      payload.filename_substring = config.filename_substring || '';
    } else if (connectionType === 'api') {
      payload.api_endpoint = config.api_endpoint;
      payload.api_key = config.api_key || '';

      const authType = config.auth_type || 'none';
      const method = (config.method || 'GET').toUpperCase();
      const nextTransform = { ...(payload.transform_params || {}) };

      if (method !== 'GET') nextTransform.method = method;
      if (authType !== 'none') {
        nextTransform.auth_type = authType;
        if (authType === 'api_key' && config.api_key_header) {
          nextTransform.api_key_header = config.api_key_header;
        }
        if (authType === 'basic' && config.basic_username) {
          nextTransform.basic_username = config.basic_username;
        }
      }
      if (Object.keys(nextTransform).length > 0) {
        payload.transform_params = nextTransform;
      }

      // Parse body + headers JSON text inputs; silently ignore if invalid
      // (the UI blocks submission via the Test Connection disabled state,
      // but we also guard here so a bad parse never clobbers the payload).
      const parseJsonSilent = (raw) => {
        if (raw == null || String(raw).trim() === '') return null;
        try {
          const parsed = JSON.parse(raw);
          return typeof parsed === 'object' && !Array.isArray(parsed) && parsed ? parsed : null;
        } catch {
          return null;
        }
      };
      const body = parseJsonSilent(config.json_body_text);
      const headers = parseJsonSilent(config.json_headers_text);
      if (body && method !== 'GET') payload.json_body_template = body;
      if (headers) payload.json_headers = headers;

      if (config.pagination_param) payload.pagination_param = config.pagination_param;
      if (config.page_size_param) payload.page_size_param = config.page_size_param;
      if (config.initial_page !== undefined && config.initial_page !== '') {
        const parsed = parseInt(config.initial_page, 10);
        if (!Number.isNaN(parsed)) payload.initial_page = parsed;
      }
    } else if (connectionType === 'google_sheet') {
      payload.spreadsheet_id = config.spreadsheet_id;
      payload.worksheet_range = config.worksheet_range || '';
    } else if (connectionType === 'sharepoint') {
      payload.site_url = config.site_url;
      payload.folder_path = config.folder_path;
      payload.filename_substring = config.filename_substring || 'Leads_';
    }

    try {
      if (connectionType === 'manual') {
        // Manual: skip mapping CRUD and just run the import now.
        const form = new FormData();
        form.append('file', sampleFile);
        form.append('client', selectedClient?.id);
        form.append('column_mappings', JSON.stringify(cleanMappings));
        form.append('transform_params', JSON.stringify(transformParams));
        await processManualUpload(form);
      } else {
        await createMapping(connectionType, payload);
      }
      clearWizardDraft(draftUserId);
      navigate(paths.dataIntegrations.health);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === 'object'
          ? JSON.stringify(err.response.data)
          : err?.message || 'Failed to create integration.');
      setSaveError(detail);
    } finally {
      setSaving(false);
    }
  }, [connectionType, config, columnMappings, dateTransforms, selectedClient, sampleFile, navigate, draftUserId]);

  const canProceed = useMemo(() => {
    if (activeStep === 0) return !!connectionType;
    if (activeStep === STEP_CRM) return false; // CRM cards advance themselves — hide Continue
    if (activeStep === STEP_SETUP) return setupConfirmed;
    if (activeStep === STEP_DETAILS) {
      if (!selectedClient) return false;
      if (connectionType === 'email') return !!(config.email_sender && config.subject);
      if (connectionType === 'api') return !!config.api_endpoint;
      if (connectionType === 'google_sheet') return !!config.spreadsheet_id;
      if (connectionType === 'sharepoint') return !!(config.site_url && config.folder_path);
      return true;
    }
    if (activeStep === STEP_COLUMNS) {
      // Manual flow picks the client on this step; everything else picks
      // it earlier in Details.
      if (connectionType === 'manual' && !selectedClient) return false;
      const mapped = new Set(Object.values(columnMappings).filter(Boolean));
      const hasRequired = mapped.has('prospect_id') && mapped.has('created_date') && mapped.has('net_amount');
      const hasContact = mapped.has('phone') || mapped.has('email');
      return hasRequired && hasContact;
    }
    return true;
  }, [activeStep, connectionType, config, selectedClient, columnMappings, setupConfirmed, STEP_CRM, STEP_SETUP, STEP_DETAILS, STEP_COLUMNS]);

  const tour = useTour({ activeStep, connectionType, isManual, hasSetupStep });

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(paths.dataIntegrations.health)}
        >
          Back to Integrations
        </Button>
        <TourTriggerButton tour={tour} />
      </Stack>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Connect Your Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Set up a connection to automatically import your backend data.
      </Typography>

      {draftPrompt && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={resumeDraft}>
                Resume
              </Button>
              <Button color="inherit" size="small" onClick={discardDraft}>
                Start fresh
              </Button>
            </Stack>
          }
        >
          You have a saved draft
          {draftPrompt.connectionType ? ` (${draftPrompt.connectionType})` : ''}
          {draftPrompt.savedAt
            ? ` from ${new Date(draftPrompt.savedAt).toLocaleString()}`
            : ''}
          . Pick up where you left off?
        </Alert>
      )}

      <TourOverlay tour={tour} />

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        {activeStep === 0 && (
          <StepConnectionType value={connectionType} onChange={setConnectionType} isStaff={isStaff} />
        )}
        {activeStep === STEP_CRM && (
          <StepCrmChoice
            onPick={(pickedType) => {
              setConnectionType(pickedType);
              // Advance to the next step (Setup Instructions for email,
              // LP Setup for leadperfection). Both live at index 2.
              setActiveStep(2);
              // Reset LP result in case the user is bouncing between choices.
              setLpResult(null);
            }}
          />
        )}
        {activeStep === STEP_SETUP && (
          <StepSetupInstructions
            confirmed={setupConfirmed}
            onConfirm={setSetupConfirmed}
          />
        )}
        {activeStep === STEP_LP && (
          <StepLeadPerfection
            config={config}
            onChange={setConfig}
            clientOptions={clientOptions || []}
            onClientSearch={handleClientSearch}
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            isSingleClient={isSingleClient}
            isStaff={isStaff}
            currentUser={user}
            lpMeta={metadata?.leadperfection}
            submitting={lpSubmitting}
            result={lpResult}
            onSubmit={handleLpSubmit}
          />
        )}
        {activeStep === STEP_DETAILS && (
          <StepDetails
            type={connectionType}
            config={config}
            onChange={setConfig}
            clientOptions={clientOptions || []}
            onClientSearch={handleClientSearch}
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            isSingleClient={isSingleClient}
            googleServiceAccountEmail={metadata?.google_service_account_email || ''}
          />
        )}
        {activeStep === STEP_COLUMNS && (
          <>
            {/* Manual upload picks the client here since there's no Details step. */}
            {isManual && !isSingleClient && (
              <Box sx={{ mb: 3 }}>
                <Autocomplete
                  options={clientOptions}
                  getOptionLabel={(o) => o.name || ''}
                  value={selectedClient}
                  onChange={(_, v) => setSelectedClient(v)}
                  onInputChange={handleClientSearch}
                  isOptionEqualToValue={(option, val) => option.id === val?.id}
                  filterOptions={(x) => x}
                  renderInput={(p) => <TextField {...p} label="Client" required helperText="Which client is this data for?" />}
                  sx={{ maxWidth: 500 }}
                />
              </Box>
            )}
            {isManual && isSingleClient && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Importing for <strong>{selectedClient?.name}</strong>.
              </Alert>
            )}
            <StepColumnMapping
              connectionType={connectionType}
              mappings={columnMappings}
              onMappingsChange={setColumnMappings}
              headers={csvHeaders}
              onFileUpload={handleFileUpload}
              onApiSample={handleApiSample}
              apiSampleError={apiSampleError}
              apiSampleInfo={apiSampleInfo}
              backendFields={backendFields}
              fieldLabels={fieldLabels}
              uploading={uploading}
              suggestions={suggestions}
              transforms={dateTransforms}
              onTransformsChange={setDateTransforms}
              sampleValues={sampleValues}
            />
          </>
        )}
        {activeStep === STEP_PREVIEW && (
          <StepPreview
            previewData={previewData}
            loading={previewLoading}
            error={previewError}
            onRunPreview={handleRunPreview}
            isApi={connectionType === 'api'}
          />
        )}
      </Card>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      <Stack direction="row" justifyContent="space-between">
        <Button
          disabled={activeStep === 0 || (isLeadPerfection && !!lpResult)}
          onClick={() => {
            if (isLeadPerfection) {
              // Reset LP result + config on back so they can retry if needed.
              setLpResult(null);
            }
            setActiveStep((s) => s - 1);
          }}
        >
          {isLeadPerfection && lpResult ? 'Done' : 'Back'}
        </Button>
        <Box>
          {isLeadPerfection ? (
            lpResult ? (
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate(paths.dataIntegrations.health)}
              >
                Go to Integrations
              </Button>
            ) : null
          ) : activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              disabled={!canProceed}
              onClick={() => setActiveStep((s) => s + 1)}
            >
              Continue
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              disabled={saving}
              onClick={handleActivate}
            >
              {saving
                ? (isManual ? 'Importing...' : 'Activating...')
                : (isManual ? 'Import File' : 'Activate Connection')}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
